import React from 'react';
import { getLootboxItems } from '../constants/lootbox';
import { VFX_EFFECTS } from '../constants/vfx';

const RARITY_ORDER_S1 = ['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY', 'MYTHIC', 'GALAXY', 'NUCLEAR'];
const RARITY_ORDER_S2 = ['COMMON', 'UNCOMMON', 'RARE', 'EPIC', 'LEGENDARY', 'MYTHIC', 'GALAXY'];
const RARITY_COLORS = {
  COMMON: '#B0B0B0', UNCOMMON: '#5CB85C', RARE: '#5BC0DE', EPIC: '#9B59B6',
  LEGENDARY: '#FFD700', MYTHIC: '#FF1744', GALAXY: '#9C27B0', NUCLEAR: '#00FF00'
};
const BOX_LABELS = {
  normal: { name: 'Normal', icon: '🎁' },
  elite: { name: 'Elite', icon: '👑' },
  god: { name: 'God Box', icon: '⚡' },
  shiny: { name: 'Shiny', icon: '✨' },
  stattrak: { name: 'StatTrak', icon: '📊' },
  vfxbox: { name: 'VFX', icon: '✦' },
};

function UnboxingStatsContent({ unboxingStatsAll, isS2 }) {
  const RARITY_ORDER = isS2 ? RARITY_ORDER_S2 : RARITY_ORDER_S1;
  // Global aggregates
  const totalUnboxed = unboxingStatsAll.reduce((sum, e) => sum + (e.stats.totalUnboxed || 0), 0);
  const totalShiny = unboxingStatsAll.reduce((sum, e) => sum + (e.stats.shinyCount || 0), 0);
  const totalStatTrak = unboxingStatsAll.reduce((sum, e) => sum + (e.stats.statTrakCount || 0), 0);
  const totalVfx = unboxingStatsAll.reduce((sum, e) => sum + (e.stats.vfxCount || 0), 0);

  // Aggregate rarity counts
  const globalRarityCounts = {};
  RARITY_ORDER.forEach(r => { globalRarityCounts[r] = 0; });
  unboxingStatsAll.forEach(e => {
    const rc = e.stats.rarityCounts || {};
    RARITY_ORDER.forEach(r => {
      globalRarityCounts[r] += rc[r.toLowerCase()] || 0;
    });
  });

  // Aggregate box type counts
  const globalBoxCounts = {};
  Object.keys(BOX_LABELS).forEach(k => { globalBoxCounts[k] = 0; });
  unboxingStatsAll.forEach(e => {
    const lc = e.stats.lootboxTypeCounts || {};
    Object.keys(BOX_LABELS).forEach(k => {
      globalBoxCounts[k] += lc[k] || 0;
    });
  });

  // Leaderboards
  const mostOpened = [...unboxingStatsAll]
    .filter(e => (e.stats.totalUnboxed || 0) > 0)
    .sort((a, b) => (b.stats.totalUnboxed || 0) - (a.stats.totalUnboxed || 0))
    .slice(0, 5);

  // Collection leaderboards — compute per-player collection percentages
  const totalItemCount = getLootboxItems(isS2).length;
  const totalVfxEffects = VFX_EFFECTS.length;

  const playerCollections = unboxingStatsAll.map(e => {
    const inv = e.inventory || [];
    const ownedIds = new Set();
    const shinyIds = new Set();
    const stIds = new Set();
    const vfxIds = new Set();
    const vfxEffectIds = new Set();
    inv.forEach(item => {
      if (item.id) ownedIds.add(item.id);
      if (item.id && item.isShiny) shinyIds.add(item.id);
      if (item.id && item.isStatTrak) stIds.add(item.id);
      if (item.id && item.vfx && Array.isArray(item.vfx) && item.vfx.length > 0) {
        vfxIds.add(item.id);
        item.vfx.forEach(v => vfxEffectIds.add(v));
      }
    });
    return {
      name: e.player.name,
      base: { collected: ownedIds.size, total: totalItemCount, pct: totalItemCount > 0 ? parseFloat(((ownedIds.size / totalItemCount) * 100).toFixed(1)) : 0 },
      shiny: { collected: shinyIds.size, total: totalItemCount, pct: totalItemCount > 0 ? parseFloat(((shinyIds.size / totalItemCount) * 100).toFixed(1)) : 0 },
      stattrak: { collected: stIds.size, total: totalItemCount, pct: totalItemCount > 0 ? parseFloat(((stIds.size / totalItemCount) * 100).toFixed(1)) : 0 },
      vfx: { collected: vfxIds.size + vfxEffectIds.size, total: totalItemCount + totalVfxEffects, pct: (totalItemCount + totalVfxEffects) > 0 ? parseFloat((((vfxIds.size + vfxEffectIds.size) / (totalItemCount + totalVfxEffects)) * 100).toFixed(1)) : 0 },
    };
  });

  const collectionLeaderboards = [
    { key: 'base', title: 'Base Collection', icon: '📊', color: '#5BC0DE',
      entries: playerCollections.filter(p => p.base.collected > 0).sort((a, b) => b.base.pct - a.base.pct).slice(0, 5).map(p => ({ ...p.base, name: p.name })) },
    { key: 'shiny', title: 'Shiny Collection', icon: '✨', color: '#FFD700',
      entries: playerCollections.filter(p => p.shiny.collected > 0).sort((a, b) => b.shiny.pct - a.shiny.pct).slice(0, 5).map(p => ({ ...p.shiny, name: p.name })) },
    { key: 'stattrak', title: 'StatTrak Collection', icon: '📊', color: '#FF8C00',
      entries: playerCollections.filter(p => p.stattrak.collected > 0).sort((a, b) => b.stattrak.pct - a.stattrak.pct).slice(0, 5).map(p => ({ ...p.stattrak, name: p.name })) },
    { key: 'vfx', title: 'VFX Collection', icon: '✦', color: '#a78bfa',
      entries: playerCollections.filter(p => p.vfx.collected > 0).sort((a, b) => b.vfx.pct - a.vfx.pct).slice(0, 5).map(p => ({ ...p.vfx, name: p.name })) },
  ];

  const panelStyle = {
    background: 'var(--bg-card)',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  };
  const panelHeader = (icon, text) => (
    <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ fontSize: '18px' }}>{icon}</span>
      <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{text}</span>
    </div>
  );
  const panelBody = { padding: '20px' };

  const rankedRow = (rank, name, detail, value, valueColor, isLast = false) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '12px 0',
      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{ width: '28px', textAlign: 'center', fontSize: '15px', fontWeight: '800', color: rank <= 3 ? '#FBBF24' : 'var(--text-dim)' }}>
        {rank}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>{name}</div>
        {detail && <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>{detail}</div>}
      </div>
      <span style={{ fontSize: '18px', fontWeight: '800', color: valueColor, flexShrink: 0 }}>{value}</span>
    </div>
  );

  return (
    <>
      {/* Overview Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '12px',
        marginBottom: '24px',
      }}>
        {[
          { label: 'Total Unboxed', value: totalUnboxed.toLocaleString(), color: '#FBBF24' },
          { label: 'Shiny Items', value: totalShiny.toLocaleString(), sub: totalUnboxed > 0 ? `${((totalShiny / totalUnboxed) * 100).toFixed(1)}%` : '', color: '#FFD700' },
          { label: 'StatTrak Items', value: totalStatTrak.toLocaleString(), sub: totalUnboxed > 0 ? `${((totalStatTrak / totalUnboxed) * 100).toFixed(1)}%` : '', color: '#FF8C00' },
          { label: 'VFX Items', value: totalVfx.toLocaleString(), sub: totalUnboxed > 0 ? `${((totalVfx / totalUnboxed) * 100).toFixed(1)}%` : '', color: '#a78bfa' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderLeft: `3px solid ${stat.color}`,
            borderRadius: '8px',
            padding: '16px 20px',
          }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{stat.label}</div>
            <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)', fontFamily: "'Orbitron', sans-serif" }}>{stat.value}</div>
            {stat.sub && <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>{stat.sub}</div>}
          </div>
        ))}
      </div>

      {/* Rarity Breakdown + Box Types side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div style={panelStyle}>
          {panelHeader('💎', 'Rarity Breakdown')}
          <div style={panelBody}>
            {RARITY_ORDER.map((r, idx) => {
              const count = globalRarityCounts[r];
              const pct = totalUnboxed > 0 ? ((count / totalUnboxed) * 100) : 0;
              return (
                <div key={r} style={{ marginBottom: idx < RARITY_ORDER.length - 1 ? '10px' : 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', color: RARITY_COLORS[r], fontWeight: '700' }}>{r}</span>
                    <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '700' }}>{count.toLocaleString()} <span style={{ color: 'var(--text-dim)' }}>({pct.toFixed(1)}%)</span></span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: RARITY_COLORS[r], borderRadius: '3px', transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={panelStyle}>
          {panelHeader('🎁', 'Boxes by Type')}
          <div style={panelBody}>
            {Object.entries(BOX_LABELS).map(([key, { name, icon }], idx) => {
              const count = globalBoxCounts[key];
              const pct = totalUnboxed > 0 ? ((count / totalUnboxed) * 100) : 0;
              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '10px 0',
                  borderBottom: idx < Object.keys(BOX_LABELS).length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                }}>
                  <span style={{ fontSize: '20px', width: '28px', textAlign: 'center' }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>{name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{pct.toFixed(1)}%</div>
                  </div>
                  <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)', fontFamily: "'Orbitron', sans-serif" }}>{count.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Collection Leaderboards (2x2 grid) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {collectionLeaderboards.map(lb => (
          <div key={lb.key} style={panelStyle}>
            {panelHeader(lb.icon, lb.title)}
            <div style={panelBody}>
              {lb.entries.length > 0 ? lb.entries.map((e, i) => (
                rankedRow(i + 1, e.name, `${e.collected}/${e.total}`, `${e.pct}%`, lb.color, i === lb.entries.length - 1)
              )) : (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>No collection data yet.</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Most Boxes Opened */}
      <div style={{ ...panelStyle, marginBottom: '24px' }}>
        {panelHeader('🏆', 'Most Boxes Opened')}
        <div style={panelBody}>
          {mostOpened.length > 0 ? mostOpened.map((e, i) => (
            rankedRow(i + 1, e.player.name, null, (e.stats.totalUnboxed || 0).toLocaleString(), '#FFD700', i === mostOpened.length - 1)
          )) : (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>No unboxing data yet.</div>
          )}
        </div>
      </div>
    </>
  );
}

export default UnboxingStatsContent;
